import type { UserRole, WorkspaceProduct } from "../api/types";

export type NavigationId =
  | "dashboard"
  | "leads"
  | "new-lead"
  | "followups"
  | "projects"
  | "workflows"
  | "quotations"
  | "commerce"
  | "revenue"
  | "reports"
  | "inventory"
  | "simple-billing"
  | "integrations"
  | "gtm"
  | "subscription"
  | "admin";

export type NavigationIcon = "dashboard" | "users" | "plus" | "calendar" | "workflow" | "document" | "commerce" | "chart" | "revenue" | "plug" | "inventory" | "billing" | "settings" | "target";

export interface NavigationItem {
  id: NavigationId;
  label: string;
  path: string;
  icon: NavigationIcon;
}

const dashboard: NavigationItem = { id: "dashboard", label: "Dashboard", path: "/dashboard", icon: "dashboard" };
const inventory: NavigationItem = { id: "inventory", label: "Inventory", path: "/simple-billing/inventory", icon: "inventory" };
const simpleBilling: NavigationItem = { id: "simple-billing", label: "Billing", path: "/simple-billing", icon: "billing" };
const customWorkflows: NavigationItem = { id: "workflows", label: "Workflows", path: "/workflows", icon: "workflow" };
const commerce: NavigationItem = { id: "commerce", label: "Commerce", path: "/commerce", icon: "commerce" };
const revenue: NavigationItem = { id: "revenue", label: "Revenue", path: "/revenue", icon: "revenue" };
const billingWorkspaceNavigation: NavigationItem[] = [
  simpleBilling,
  { id: "admin", label: "Team", path: "/admin", icon: "users" },
  { id: "subscription", label: "Subscription", path: "/subscription", icon: "billing" },
];
const clientNavigation: NavigationItem[] = [
  dashboard,
  { id: "leads", label: "Leads", path: "/leads", icon: "users" },
  { id: "new-lead", label: "New Lead", path: "/new-lead", icon: "plus" },
  { id: "followups", label: "Follow-ups", path: "/followups", icon: "calendar" },
  { id: "quotations", label: "Quotations", path: "/quotations", icon: "document" },
  { id: "reports", label: "Reports", path: "/reports", icon: "chart" },
];

const ownerManagerNavigation: NavigationItem[] = [
  ...clientNavigation,
  { id: "integrations", label: "Integrations", path: "/integrations", icon: "plug" },
  { id: "subscription", label: "Subscription", path: "/subscription", icon: "billing" },
  { id: "admin", label: "Team & Setup", path: "/admin", icon: "settings" },
];

const superAdminNavigation: NavigationItem[] = [
  dashboard,
  { id: "admin", label: "Clients", path: "/admin", icon: "settings" },
  { id: "gtm", label: "GTM", path: "/gtm", icon: "target" },
  { id: "quotations", label: "Quotations", path: "/quotations", icon: "document" },
  { id: "subscription", label: "Subscriptions", path: "/subscription", icon: "billing" },
  { id: "integrations", label: "Integrations", path: "/integrations", icon: "plug" },
];

export function navigationForRole(
  role: UserRole,
  features: readonly string[] = [],
  workspaceProduct: WorkspaceProduct | null = null,
  inventoryEnabled = false,
): NavigationItem[] {
  if (workspaceProduct === "SIMPLE_BILLING" && role !== "super_admin") {
    return inventoryEnabled ? [inventory, ...billingWorkspaceNavigation] : billingWorkspaceNavigation;
  }
  const base = role === "super_admin"
    ? superAdminNavigation
    : role === "owner" || role === "manager"
      ? ownerManagerNavigation
      : clientNavigation;
  let result = [...base];
  if (features.includes("CUSTOM_WORKFLOWS")) {
    const followupsIndex = result.findIndex((item) => item.id === "followups");
    const insertionIndex = followupsIndex >= 0 ? followupsIndex + 1 : result.length;
    result = [...result.slice(0, insertionIndex), customWorkflows, ...result.slice(insertionIndex)];
  }
  if (features.includes("REVENUE_MODULE") && role === "owner") {
    const reportsIndex = result.findIndex((item) => item.id === "reports");
    const dashboardIndex = result.findIndex((item) => item.id === "dashboard");
    const insertionIndex = reportsIndex >= 0 ? reportsIndex : dashboardIndex >= 0 ? dashboardIndex + 1 : result.length;
    result = [...result.slice(0, insertionIndex), revenue, ...result.slice(insertionIndex)];
  }
  if (
    features.includes("LEADCAT_COMMERCE")
    && (role === "owner" || role === "manager" || role === "staff" || role === "super_admin")
  ) {
    const quotationsIndex = result.findIndex((item) => item.id === "quotations");
    const insertionIndex = quotationsIndex >= 0 ? quotationsIndex + 1 : result.length;
    result = [...result.slice(0, insertionIndex), commerce, ...result.slice(insertionIndex)];
  }
  if (!features.includes("SIMPLE_BILLING")) return result;
  const reportsIndex = result.findIndex((item) => item.id === "reports");
  const insertionIndex = reportsIndex >= 0 ? reportsIndex + 1 : result.length;
  const billingItems = inventoryEnabled ? [inventory, simpleBilling] : [simpleBilling];
  return [...result.slice(0, insertionIndex), ...billingItems, ...result.slice(insertionIndex)];
}

export function activeNavigationId(pathname: string): NavigationId {
  if (pathname === "/simple-billing/inventory") return "inventory";
  if (pathname.startsWith("/leads/")) return "leads";
  if (pathname === "/projects" || pathname.startsWith("/projects/")) return "workflows";
  if (pathname === "/meta-setup") return "integrations";
  if (pathname === "/setup") return "admin";
  const segment = pathname.replace(/^\//, "").split("/")[0];
  return (segment || "dashboard") as NavigationId;
}

export function titleForPath(
  pathname: string,
  role: UserRole,
  features: readonly string[] = [],
  workspaceProduct: WorkspaceProduct | null = null,
): string {
  if (pathname === "/gtm") return "GTM Dashboard";
  if (pathname === "/gtm/prospects/new") return "Add Prospect";
  if (pathname.startsWith("/gtm/prospects/")) return "Prospect Details";
  if (pathname === "/gtm/prospects") return "Prospects";
  if (pathname === "/gtm/discover") return "Discover Businesses";
  if (pathname === "/gtm/today") return "Today";
  if (pathname === "/gtm/analytics") return "GTM Analytics";
  if (pathname === "/gtm/calling-queue") return "Calling Queue";
  if (pathname.startsWith("/leads/")) return "Lead Detail";
  if (pathname === "/quotations/new") return "New Quotation";
  if (pathname.startsWith("/quotations/")) return "Quotation";
  if (pathname === "/leads") return "Leads";
  if (pathname === "/followups") return "Follow-ups";
  if (pathname === "/revenue") return "Revenue";
  if (pathname === "/projects") return "Workflows";
  if (pathname.startsWith("/projects/")) return "Project Detail";
  if (pathname === "/workflows") return role === "staff" ? "My Workflow" : "Workflow Operations";
  if (pathname === "/workflows/templates") return "Workflow Templates";
  if (pathname === "/workflows/reports") return "Workflow Reports";
  if (pathname.startsWith("/workflows/projects/")) return "Project Detail";
  if (pathname === "/admin/workflow-launch") return "Workflow Controlled Launch";
  if (pathname === "/simple-billing/inventory") return "Inventory";
  if (pathname === "/setup") return "Workspace Setup";
  if (pathname === "/meta-setup") return "Instagram/Facebook Setup";
  const active = activeNavigationId(pathname);
  return navigationForRole(role, features, workspaceProduct).find((item) => item.id === active)?.label || "Dashboard";
}

export function roleCanAccessPath(
  role: UserRole,
  pathname: string,
  workspaceProduct: WorkspaceProduct | null = null,
): boolean {
  if (workspaceProduct === "SIMPLE_BILLING" && role !== "super_admin") {
    return pathname === "/admin"
      || pathname === "/subscription"
      || pathname === "/simple-billing"
      || pathname.startsWith("/simple-billing/");
  }
  if (role === "super_admin" && (pathname === "/leads" || pathname.startsWith("/leads/") || pathname === "/new-lead" || pathname === "/followups")) return false;
  const active = activeNavigationId(pathname);
  return navigationForRole(role).some((item) => item.id === active);
}

export function workspaceHomePath(
  role: UserRole,
  workspaceProduct: WorkspaceProduct | null | undefined,
  features: readonly string[] = [],
): string {
  if (workspaceProduct !== "SIMPLE_BILLING" || role === "super_admin") return "/dashboard";
  return features.includes("SIMPLE_BILLING") ? "/simple-billing" : "/subscription";
}

export function legacyPathFor(pathname: string): string {
  const normalized = pathname.replace(/^\//, "") || "dashboard";
  if (normalized.startsWith("leads/")) return `/#lead/${normalized.slice("leads/".length)}`;
  return `/#${normalized}`;
}
